import { isDefaultDeckSurfaceHex } from '../../../deck/defaults';
import { brandColor } from '../../styles/brand-colors';
import { Path, Svg } from '../../svg';
import { useTheme } from '../../theme';
import { mixHex } from '../../utils/color';

/**
 * Flashka deck glyph — the two-decker flashcard-stack mark that identifies a
 * deck (list-view deck cards, sidebar rows). Bespoke (not in lucide), drawn
 * once with the shared SVG primitives so it renders identically on web +
 * native. Paths are lifted from flashka-web's legacy
 * `src/components/icons/deck.tsx` (20×20 viewBox).
 *
 * Two-tone: `color` fills the body; the darker outline/detail defaults to
 * `color` mixed 20% toward black — the same `color-mix` math the legacy web
 * icon applies to custom deck colours. Pass `detailColor` to override (e.g.
 * a named deck palette's `-800` twin).
 *
 * Dark follows web's `components/icons/deck.tsx`: a colour darkens toward
 * black (body 60%, detail 40%) so it doesn't glare on the dark page, and the
 * neutral default ({@link isDefaultDeckSurfaceHex}) draws outline-only in the
 * flipping `secondary-600`, never as a filled slab of the card surface colour.
 */
export function DeckIcon({
  color = '#60a5fa',
  detailColor,
  size = 20,
  className,
}: {
  color?: string;
  detailColor?: string;
  size?: number;
  /** Forwarded to the svg — e.g. Badge/Button pass `shrink-0`. */
  className?: string;
}) {
  const { isDark } = useTheme();
  const neutral = isDefaultDeckSurfaceHex(color);
  const body = neutral ? null : isDark ? mixHex(color, 'black', 0.6) : color;
  const detail = neutral
    ? brandColor('secondary', '600', isDark)
    : (detailColor ?? mixHex(color, 'black', isDark ? 0.4 : 0.2));
  return (
    <Svg width={size} height={size} viewBox="0 0 20 20" fill="none" className={className}>
      {body && (
        <Path
          fill={body}
          d="M2 5.5A3.5 3.5 0 0 1 5.5 2h9.333a3.5 3.5 0 0 1 3.5 3.5v9.333a3.5 3.5 0 0 1-3.5 3.5H5.5a3.5 3.5 0 0 1-3.5-3.5V5.5Z"
        />
      )}
      <Path
        fill={detail}
        fillRule="evenodd"
        clipRule="evenodd"
        d="M14.833 18.333H5.5l-.18-.004a3.5 3.5 0 0 1-3.315-3.315L2 14.833V5.5a3.5 3.5 0 0 1 3.32-3.495L5.5 2h9.333l.18.005a3.5 3.5 0 0 1 3.32 3.495v9.333l-.004.18a3.5 3.5 0 0 1-3.315 3.316l-.181.004Zm0-1.552H5.5a1.948 1.948 0 0 1-1.948-1.948v-3.89h13.23v3.89a1.948 1.948 0 0 1-1.949 1.948Zm0-13.23c1.076 0 1.948.873 1.948 1.949v3.89H3.551V5.5c0-1.076.873-1.948 1.949-1.948h9.333Z"
      />
    </Svg>
  );
}
